import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';

/**
 * Le clavier de l'application native.
 *
 * Deux choses s'y reglent, qu'aucune feuille de style ne peut atteindre.
 *
 * **La barre d'assistance.** Sur iPhone, WKWebView coiffe tout clavier de
 * deux chevrons et d'un « OK », dessines par le systeme sur fond clair.
 * C'est le bandeau blanc qui s'intercalait entre le composeur et le
 * clavier, et que le mode nuit rendait criant. Aucune messagerie ne
 * l'affiche : elle sert a parcourir un formulaire, pas a ecrire.
 *
 * **La place qu'il prend.** Capacitor sait retailler la vue web, mais
 * seulement une fois le clavier arrive (voir `capacitor.config.ts`). On
 * prend donc la hauteur des l'annonce de la montee et on la pose sur la
 * racine : `#root` s'en sert pour se retirer d'autant, sur la meme duree.
 *
 * **Et le doigt.** La page se retirait d'un trait quand le clavier, lui,
 * descendait avec le doigt (`AppDelegate.swift`) : le composeur restait
 * en l'air jusqu'a ce que le geste s'acheve, puis tombait d'un coup.
 *
 * Quatre voies natives ont ete essayees pour connaitre la position du
 * clavier en cours de route, et toutes retirees — elles sont consignees
 * dans `AppDelegate.swift`. Celle-ci ne l'est pas : le **viewport
 * visuel** de la page connait deja cette position. Le clavier le retaille
 * a mesure qu'il descend, et il l'annonce a chaque image. Il ne restait
 * qu'a l'ecouter.
 *
 * Cela ne marche que parce que la fenetre garde toute sa hauteur
 * (`resize: 'none'`, voir `capacitor.config.ts`) : c'est ce qui laisse le
 * viewport visuel se retrecir sous le clavier plutot que de suivre une
 * fenetre deja retaillee.
 *
 * Sur le web, il n'y a pas de clavier a piloter : la fonction ne fait rien.
 *
 * **Sur Android non plus, et c'est verifie** : la fenetre y est retaillee
 * par le systeme a l'arrivee du clavier — le composeur se pose dessus
 * tout seul, comme dans Chrome. Ajouter notre translation par-dessus
 * faisait deux corrections pour un clavier : le composeur sautait en
 * haut de l'ecran, a une hauteur de clavier au-dessus de sa place.
 */
export function setUpNativeKeyboard(): void {
  if (Capacitor.getPlatform() !== 'ios') return;

  void Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(() => {
    // Plugin absent ou iPad : l'application reste utilisable, avec la barre.
  });

  const root = document.documentElement;

  /*
    Tant que cet instant n'est pas passe, la transition CSS a la main :
    c'est le systeme qui anime la montee et la descente, et se mettre a
    suivre le viewport au meme moment ferait deux mouvements pour un.
  */
  let animeJusqua = 0;
  /** Le retrait quand le clavier est entierement monte. */
  let liftPlein = 0;

  function anime(lift: number) {
    root.style.setProperty('--keyboard-lift', `${lift}px`);
    root.style.setProperty('--keyboard-drag', '0px');
    animeJusqua = Date.now() + ANIMATION_MS;
  }

  void Keyboard.addListener('keyboardWillShow', (info) => {
    // La zone sure ne change qu'a la rotation : une mesure par montee suffit.
    safeArea = safeAreaBottom();
    liftPlein = liftFor(info.keyboardHeight);
    anime(liftPlein);
  });

  void Keyboard.addListener('keyboardWillHide', () => anime(0));

  /*
    Le doigt, enfin — et sans toucher a la mise en page.

    Entre les deux annonces du systeme, le viewport visuel est le seul a
    savoir ou en est le clavier, et il le dit a chaque image. Mais on ne
    peut pas s'en servir pour changer le retrait de la page : cela
    retaille les zones defilantes, qui se recalent aussitot sur leur bas —
    et UIKit abandonne son glissement de clavier des qu'on touche a un
    defilement. Le clavier tombait alors d'un coup, sans qu'on puisse le
    remonter.

    On ne deplace donc que le composeur, par une `transform` : elle ne
    change aucune taille, ne provoque aucun recalage, et le geste du
    systeme se poursuit intact. Le fil, lui, n'a pas besoin de bouger —
    la place qui se decouvre est celle que le clavier occupait.
  */
  const vue = window.visualViewport;
  if (!vue) return;

  let image = 0;
  const suivre = () => {
    if (Date.now() < animeJusqua || image) return;
    image = requestAnimationFrame(() => {
      image = 0;
      // Ce que le clavier recouvre : la fenetre garde sa hauteur pleine,
      // le viewport visuel non.
      const recouvert = Math.max(0, window.innerHeight - (vue.height + vue.offsetTop));
      const restant = Math.max(0, recouvert - safeArea);
      root.style.setProperty('--keyboard-drag', `${Math.max(0, liftPlein - restant)}px`);
    });
  };

  vue.addEventListener('resize', suivre);
  vue.addEventListener('scroll', suivre);
}

/**
 * Le temps qu'une montee ou une descente du systeme s'anime seule.
 *
 * A tenir au-dessus de `--keyboard-lift-duration` : tant que la
 * transition CSS n'a pas fini, se remettre a suivre le viewport ferait
 * deux mouvements pour un.
 */
const ANIMATION_MS = 320;

/** La zone sure du bas, relevee a chaque montee de clavier. */
let safeArea = 0;

/**
 * De combien la page doit se retirer pour un clavier de cette hauteur.
 *
 * On retranche la zone sure du bas. Les gabarits la reservent deja
 * (`.tabbar`, `.sub-screen`, `.ms-composer`...) et, la vue web gardant
 * toute la hauteur de l'ecran, `env()` vaut toujours l'indicateur
 * d'accueil : sans cette soustraction, tout flotterait 34 pt au-dessus
 * des touches.
 *
 * Le calcul se fait ici, en pixels, plutot qu'en `calc()` dans la feuille
 * de style : une transition entre deux longueurs franches est sans
 * surprise, la meme entre deux expressions `max(...)` ne l'est pas.
 */
function liftFor(keyboardHeight: number): number {
  return Math.max(0, keyboardHeight - safeArea);
}

/**
 * `env(safe-area-inset-bottom)`, en pixels.
 *
 * Aucune interface ne le donne : on le fait mesurer a la page. La sonde
 * est relue a chaque ouverture, la zone sure changeant avec l'orientation.
 */
function safeAreaBottom(): number {
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;bottom:0;left:0;width:0;height:env(safe-area-inset-bottom,0px);visibility:hidden;pointer-events:none;';
  document.body.appendChild(probe);
  const height = probe.getBoundingClientRect().height;
  probe.remove();
  return height;
}
